import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Careers = () => {
  const { t } = useLanguage()
  const positions = [
    {
      id: 1,
      title: t('careers.positions.digital.title'),
      department: t('careers.positions.digital.department'),
      location: t('careers.positions.digital.location'),
      type: t('careers.positions.digital.type'),
      description: t('careers.positions.digital.description'),
      requirements: [
        t('careers.positions.digital.req.1'),
        t('careers.positions.digital.req.2'),
        t('careers.positions.digital.req.3'),
        t('careers.positions.digital.req.4')
      ]
    },
    {
      id: 2,
      title: t('careers.positions.compiler.title'),
      department: t('careers.positions.compiler.department'),
      location: t('careers.positions.compiler.location'),
      type: t('careers.positions.compiler.type'),
      description: t('careers.positions.compiler.description'),
      requirements: [
        t('careers.positions.compiler.req.1'),
        t('careers.positions.compiler.req.2'),
        t('careers.positions.compiler.req.3'),
        t('careers.positions.compiler.req.4')
      ]
    },
    {
      id: 3,
      title: t('careers.positions.ai.title'),
      department: t('careers.positions.ai.department'),
      location: t('careers.positions.ai.location'),
      type: t('careers.positions.ai.type'),
      description: t('careers.positions.ai.description'),
      requirements: [
        t('careers.positions.ai.req.1'),
        t('careers.positions.ai.req.2'),
        t('careers.positions.ai.req.3'),
        t('careers.positions.ai.req.4')
      ]
    },
    {
      id: 4,
      title: t('careers.positions.cae.title'),
      department: t('careers.positions.cae.department'),
      location: t('careers.positions.cae.location'),
      type: t('careers.positions.cae.type'),
      description: t('careers.positions.cae.description'),
      requirements: [
        t('careers.positions.cae.req.1'),
        t('careers.positions.cae.req.2'),
        t('careers.positions.cae.req.3'),
        t('careers.positions.cae.req.4')
      ]
    },
    {
      id: 5,
      title: t('careers.positions.architect.title'),
      department: t('careers.positions.architect.department'),
      location: t('careers.positions.architect.location'),
      type: t('careers.positions.architect.type'),
      description: t('careers.positions.architect.description'),
      requirements: [
        t('careers.positions.architect.req.1'),
        t('careers.positions.architect.req.2'),
        t('careers.positions.architect.req.3'),
        t('careers.positions.architect.req.4')
      ]
    }
  ]

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-careers.jpg"
            placeholder="/images/placeholders/hero-careers-placeholder.jpg"
            alt="Team Collaboration"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">CAREERS</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('careers.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('careers.hero.subtitle')}
            </motion.p>
            <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-4">
              <a href="#positions" className="section-cta group">
                <span>{t('careers.hero.cta.positions')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="mailto:hr@xdt.com" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
                <span>{t('careers.hero.cta.apply')}</span>
                <span className="material-symbols-outlined text-lg">mail</span>
              </a>
            </motion.div>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Why Join Us - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">WHY JOIN US</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('careers.why.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('careers.why.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'target', title: t('careers.culture.innovation.title'), desc: t('careers.culture.innovation.desc') },
              { icon: 'group', title: t('careers.culture.collaboration.title'), desc: t('careers.culture.collaboration.desc') },
              { icon: 'psychology', title: t('careers.culture.growth.title'), desc: t('careers.culture.growth.desc') },
              { icon: 'code', title: t('careers.culture.quality.title'), desc: t('careers.culture.quality.desc') }
            ].map((item, index) => (
              <GlareCard key={index} className="text-center">
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{item.icon}</span>
                <h3 className="section-title mb-4">{item.title}</h3>
                <p className="section-desc">{item.desc}</p>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* Job Positions - 渐变纹理背景 */}
      <section id="positions" className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">OPEN POSITIONS</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('careers.positions.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('careers.positions.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {positions.map((position) => (
              <motion.div
                key={position.id}
                variants={staggerItem}
                className="metal-surface p-8 hover:border-white/20 transition-colors"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                      <h3 className="text-lg font-bold text-white mb-2 sm:mb-0 uppercase tracking-widest">
                        {position.title}
                      </h3>
                      <div className="flex items-center space-x-4 text-xs text-gray-500 font-mono">
                        <div className="flex items-center space-x-1">
                          <span className="material-symbols-outlined text-white/30 text-sm">location_on</span>
                          <span>{position.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="material-symbols-outlined text-white/30 text-sm">schedule</span>
                          <span>{position.type}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 border border-white/10 text-gray-400 text-[10px] font-mono uppercase tracking-widest">
                        {position.department}
                      </span>
                    </div>

                    <p className="text-gray-500 text-sm font-mono mb-6">
                      {position.description}
                    </p>

                    <div>
                      <h4 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{t('careers.labels.requirements')}</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start space-x-3">
                            <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                            <span className="text-gray-500 text-sm font-mono">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="lg:col-span-1">
                    <div className="border border-white/10 p-6">
                      <h4 className="font-bold text-white mb-4 uppercase tracking-widest text-sm">{t('careers.labels.benefits')}</h4>
                      <ul className="space-y-3 text-sm text-gray-500 font-mono">
                        <li className="flex items-center space-x-2">
                          <span className="material-symbols-outlined text-white/30 text-sm">payments</span>
                          <span>{t('careers.labels.competitive')}</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="material-symbols-outlined text-white/30 text-sm">trending_up</span>
                          <span>{t('careers.labels.equity')}</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="material-symbols-outlined text-white/30 text-sm">schedule</span>
                          <span>{t('careers.labels.flexible')}</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="material-symbols-outlined text-white/30 text-sm">school</span>
                          <span>{t('careers.labels.training')}</span>
                        </li>
                      </ul>

                      <div className="mt-6">
                        <a
                          href={`mailto:hr@xdt.com?subject=申请职位：${position.title}`}
                          className="section-cta w-full text-center justify-center"
                        >
                          <span>{t('careers.labels.apply')}</span>
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Company Culture - 深灰背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-dark-gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">CULTURE</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('careers.company.culture.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm">
              {t('careers.company.culture.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <motion.div variants={staggerItem} className="metal-surface p-8 hover:border-white/20 transition-colors">
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('careers.company.innovation.title')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('careers.company.innovation.desc')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-8 hover:border-white/20 transition-colors">
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('careers.company.collaboration.title')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('careers.company.collaboration.desc')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-8 hover:border-white/20 transition-colors">
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('careers.company.learning.title')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('careers.company.learning.desc')}
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-8 hover:border-white/20 transition-colors">
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('careers.company.mission.title')}</h3>
              <p className="text-gray-500 text-sm font-mono leading-relaxed">
                {t('careers.company.mission.desc')}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - 点阵纹理背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dot-texture">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">JOIN THE TEAM</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('careers.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('careers.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="mailto:hr@xdt.com" className="section-cta group">
              <span>{t('careers.cta.contact')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link to="/contact" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('careers.cta.more')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Careers
